import React, { useState } from 'react';
import {
  MapPin,
  Mail,
  Send,
  CheckCircle,
  Loader2,
  AlertCircle,
  MessageCircle,
} from 'lucide-react';

// UI Components
const InputGroup = ({ label, ...props }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-bold uppercase tracking-wider text-gray-500 ml-1">
      {label}
    </label>
    <input
      required
      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 text-gray-900 rounded-lg focus:outline-none focus:bg-white focus:border-exiser-primary focus:ring-4 focus:ring-purple-500/10 transition-all duration-200 placeholder:text-gray-400 disabled:opacity-60 disabled:cursor-not-allowed"
      {...props}
    />
  </div>
);

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  // ✅ CRA ENV (must be defined in .env)
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Enforce 10 digits max for phone
    if (name === 'phone') {
      // Allow only numbers and max 10 chars
      if (!/^\d*$/.test(value)) return;
      if (value.length > 10) return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (formData.phone.length !== 10) {
      alert('Please enter a valid 10-digit phone number.');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to connect to the server.');
      }

      setStatus('success');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Submission Error:', error);
      setStatus('error');
      setErrorMessage(
        error.message || 'Something went wrong. Please try again later.'
      );
    }
  };

  // Common WhatsApp link
  const whatsappLink =
    'https://wa.me/919973923399?text=Hi%20Exiser%20Team%2C%20I%20would%20like%20to%20know%20more%20about...';

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* ================= HERO HEADER ================= */}
      <div className="bg-exiser-primary relative overflow-hidden">
        {/* Abstract Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" />
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-white uppercase tracking-tight mb-4 drop-shadow-sm">
            Contact Us
          </h1>
          <p className="text-lg md:text-xl text-purple-100 max-w-2xl mx-auto font-light leading-relaxed">
            Ready to start your journey? We are here to help you every step of the way.
          </p>
        </div>
      </div>

      {/* ================= MAIN CONTENT ================= */}
      <div className="flex-grow max-w-7xl mx-auto px-4 py-16 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">

          {/* -------- LEFT COLUMN: INFO (Equal Width) -------- */}
          <div className="lg:col-span-6 space-y-10">

            {/* Intro Text */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 uppercase tracking-wide mb-4 border-l-4 border-exiser-accent pl-4">
                Get In Touch
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Whether you have questions about our coaching programs, need technical support, or just want to say hello, we'd love to hear from you.
              </p>
            </div>

            {/* Contact Details Cards */}
            <div className="space-y-4">
              {/* HQ */}
              <div className="flex p-4 bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-shadow group">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 bg-purple-50 rounded-full flex items-center justify-center text-exiser-primary group-hover:bg-exiser-primary group-hover:text-white transition-colors">
                    <MapPin className="w-6 h-6" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 uppercase mb-1">Headquarters</h3>
                  <p className="text-gray-600 text-base">Exiser Pvt Ltd.<br />Raja Bazar, Patna - 800014</p>
                </div>
              </div>

              {/* WhatsApp Support (Replaces Phone Display) */}
              <div className="flex p-4 bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-shadow group">
                <div className="flex-shrink-0 mr-4">
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center text-green-600 group-hover:bg-green-600 group-hover:text-white transition-colors"
                  >
                    <MessageCircle className="w-6 h-6" />
                  </a>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 uppercase mb-1">
                    WhatsApp / Phone
                  </h3>
                  <div className="flex flex-col gap-1 mb-1">
                    <a
                      href={whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 text-base font-bold hover:underline inline-flex items-center gap-2"
                    >
                      Chat with us
                    </a>
                    <a
                      href="tel:+919973923399"
                      className="text-gray-700 text-base font-medium hover:text-exiser-primary hover:underline transition-colors block"
                    >
                      +91 9973923399
                    </a>
                  </div>
                  <p className="text-gray-400 text-sm">Mon-Sat, 10am - 6pm</p>
                </div>
              </div>

              {/* Email */}
              <div className="flex p-4 bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-shadow group">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                    <Mail className="w-6 h-6" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 uppercase mb-1">Email</h3>
                  <a href="mailto:connect@exiser.in" className="text-blue-600 text-base hover:underline">connect@exiser.in</a>
                </div>
              </div>
            </div>

            {/* Map Embed */}
            <div className="relative w-full h-64 bg-gray-200 rounded-xl overflow-hidden shadow-inner border border-gray-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3597.892019875958!2d85.08552147612716!3d25.60848031491756!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39ed57963695d765%3A0x16357b23b0842304!2sRaja%20Bazar%2C%20Indrapuri%2C%20Patna%2C%20Bihar%20800014!5e0!3m2!1sen!2sin!4v1714567890000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen={true}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="grayscale hover:grayscale-0 transition-all duration-700"
                title="Exiser Location"
              ></iframe>
            </div>

          </div>

          {/* -------- RIGHT COLUMN: FORM (Equal Width) -------- */}
          <div className="lg:col-span-6">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="p-8 md:p-10">

                {status === 'success' ? (
                  /* Success State */
                  <div className="flex flex-col items-center justify-center text-center py-16 animate-in fade-in zoom-in duration-300">
                    <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-6 text-green-600 shadow-sm">
                      <CheckCircle className="w-12 h-12" />
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">Message Sent!</h3>
                    <p className="text-gray-600 max-w-md mx-auto mb-10 text-lg">
                      Thank you for reaching out. Your message has been received and we will get back to you shortly.
                    </p>
                    <button
                      onClick={() => setStatus('idle')}
                      className="px-8 py-3 bg-white border-2 border-exiser-primary text-exiser-primary font-bold rounded-full hover:bg-exiser-primary hover:text-white transition-all duration-300 uppercase tracking-wide text-sm"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  /* Form State */
                  <form onSubmit={handleSubmit} className="space-y-8">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">Send us a Message</h3>
                    </div>

                    {status === 'error' && (
                      <div className="bg-red-50 border border-red-100 text-red-700 px-6 py-4 rounded-xl flex items-start gap-4">
                        <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-bold">Submission Failed</p>
                          <p className="text-sm">{errorMessage || 'Something went wrong. Please try again.'}</p>
                        </div>
                      </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-6">
                      <InputGroup
                        label="Your Name"
                        name="name"
                        value={formData.name}
                        maxLength={50}
                        onChange={handleChange}
                        disabled={status === 'submitting'}
                        placeholder="John Doe"
                      />
                      <InputGroup
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={status === 'submitting'}
                        placeholder="john@example.com"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <InputGroup
                        label="Phone Number"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        disabled={status === 'submitting'}
                        placeholder="98765 43210"
                        maxLength={10}
                        inputMode="numeric"
                      />
                      <InputGroup
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        maxLength={50}
                        onChange={handleChange}
                        disabled={status === 'submitting'}
                        placeholder="Inquiry about..."
                      />
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center ml-1">
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Message</label>
                        <span className="text-xs text-gray-400 font-medium">
                          {formData.message.length}/200
                        </span>
                      </div>
                      <textarea
                        name="message"
                        required
                        rows={6}
                        maxLength={200}
                        value={formData.message}
                        onChange={handleChange}
                        disabled={status === 'submitting'}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 text-gray-900 rounded-lg focus:outline-none focus:bg-white focus:border-exiser-primary focus:ring-4 focus:ring-purple-500/10 transition-all duration-200 placeholder:text-gray-400 resize-none disabled:opacity-60 disabled:cursor-not-allowed"
                        placeholder="How can we help you achieve your goals? (Max 200 chars)"
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className="w-full group relative overflow-hidden bg-exiser-primary text-white font-bold py-4 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-70 disabled:hover:translate-y-0"
                    >
                      <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                      <span className="flex items-center justify-center gap-3 uppercase tracking-wider text-sm">
                        {status === 'submitting' ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" /> Sending...
                          </>
                        ) : (
                          <>
                            Send Message <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                          </>
                        )}
                      </span>
                    </button>

                    <p className="text-center text-xs text-gray-400 mt-4">
                      We value your privacy. Your information is safe with us.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ContactUs;
